import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { PORTALS } from "../../../utils/portalTypes";
import L from "../../../constants/baysportLabels";
import { LegalDocLink } from "../../Legal/LegalDocLink";
import { LEGAL_PUBLIC_ENABLED } from "../../../constants/legalPaths";
import { Button, Field, Input, PhoneInput, Textarea } from "../../ui";
import s from "./clubWebsite.module.css";

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function darkenHex(hex, amount = 0.15) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = (v) => Math.max(0, Math.round(v * (1 - amount)));
  return `#${[f(rgb.r), f(rgb.g), f(rgb.b)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

function Editable({
  editMode,
  value,
  placeholder,
  onChange,
  className,
  as: Tag = "p",
  multiline = false,
  maxLength,
}) {
  if (!editMode) {
    if (!value) return null;
    return <Tag className={className}>{value}</Tag>;
  }
  if (multiline) {
    return (
      <textarea
        className={`${className} ${s.editable} ${s.editableArea}`}
        value={value || ""}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={4}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }
  return (
    <input
      className={`${className} ${s.editable}`}
      value={value || ""}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange?.(e.target.value)}
    />
  );
}

function StarRating() {
  return (
    <div className={s.stars} aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={s.star}>
          ★
        </span>
      ))}
    </div>
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

const TEAM_TABS = [
  { id: "trainers", label: "Тренеры" },
  { id: "schedule", label: "Расписание" },
  { id: "gallery", label: "Галерея" },
];

/**
 * Публичная визитка клуба (шаблон SportLab).
 * editMode — CMS: тексты правятся на месте.
 */
export default function ClubWebsite({
  clinic: clinicProp = null,
  editMode = false,
  contentOverride = null,
  onContentChange,
}) {
  const [clinic, setClinic] = useState(clinicProp);
  const [doctors, setDoctors] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamTab, setTeamTab] = useState("trainers");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let cancelled = false;
    clinicAPI
      .getPublicClinicPortal()
      .then((data) => {
        if (cancelled) return;
        setClinic(data?.clinic || data?.tenant || clinicProp);
        setDoctors(Array.isArray(data?.doctors) ? data.doctors : []);
        setSpecializations(Array.isArray(data?.specializations) ? data.specializations : []);
      })
      .catch(() => {
        if (!cancelled) {
          setClinic(clinicProp || null);
          setDoctors([]);
          setSpecializations([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicProp]);

  const content = contentOverride || clinic?.website_content || {};
  const patchContent = (fields) => {
    onContentChange?.({ ...content, ...fields });
  };

  const clubName = clinic?.name || L.club;
  const accent = content.primary_color || clinic?.portal_accent_color || "#ff9f0a";
  const accentDark = darkenHex(accent, 0.18);
  const coverUrl = mediaUrl(content.hero_photo_url || clinic?.portal_cover_url);
  const logoUrl = mediaUrl(clinic?.logo_url);
  const gallery = Array.isArray(clinic?.portal_gallery)
    ? clinic.portal_gallery.map(mediaUrl).filter(Boolean)
    : [];
  const surfaceColor = content.surface_color || "#0a0a0a";

  const headlineLines =
    Array.isArray(content.headline_lines) && content.headline_lines.length
      ? content.headline_lines
      : content.headline
        ? [content.headline]
        : [clubName.toUpperCase()];
  const accentLineSet = new Set(
    Array.isArray(content.headline_accent_lines) ? content.headline_accent_lines : [0],
  );

  const stats = Array.isArray(content.stats)
    ? content.stats.filter((item) => item?.value || item?.label)
    : [];
  const features = Array.isArray(content.features) ? content.features : [];
  const reviews = Array.isArray(content.reviews) ? content.reviews : [];
  const trialBenefits = Array.isArray(content.trial_benefits) ? content.trial_benefits : [];
  const blockOrder =
    Array.isArray(content.block_order) && content.block_order.length
      ? content.block_order
      : ["features", "team", "reviews", "trial", "contacts"];

  const themeStyle = useMemo(() => {
    const rgb = hexToRgb(accent);
    const glow = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35)` : "rgba(255, 159, 10, 0.35)";
    const soft = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)` : "rgba(255, 159, 10, 0.1)";
    const border = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.19)` : "rgba(255, 159, 10, 0.19)";
    return {
      "--cw-accent": accent,
      "--cw-accent-dark": accentDark,
      "--cw-accent-glow": glow,
      "--cw-accent-soft": soft,
      "--cw-accent-border": border,
      "--cw-surface": surfaceColor,
    };
  }, [accent, accentDark, surfaceColor]);

  const eyebrow = content.eyebrow || "Спортивный клуб";
  const subheadline =
    content.subheadline ||
    clinic?.portal_description ||
    "Секция для тех, кто хочет научиться играть или прокачать навыки.";
  const ctaLabel = content.cta_label || "Пробное занятие →";
  const secondaryCtaLabel = content.secondary_cta_label || "Узнать больше";
  const footerText = content.footer_text || clinic?.portal_description || "";
  const contactsNote = content.contacts_note || "";

  const showStats = content.show_stats !== false && (editMode || stats.length > 0);
  const showFeatures =
    content.show_features !== false && (editMode || features.some((f) => f?.title || f?.text));
  const showTeam =
    content.show_team !== false &&
    (editMode || doctors.length > 0 || (content.show_gallery !== false && gallery.length > 0));
  const showReviews =
    content.show_reviews !== false && (editMode || reviews.some((r) => r?.text));
  const showForm = content.show_trial_form !== false;
  const showGallery = content.show_gallery !== false;
  const showContacts =
    editMode || clinic?.phone || clinic?.address || contactsNote || specializations.length > 0;

  const sportTags = specializations.length
    ? specializations.slice(0, 4).map((spec) => spec.name)
    : [];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (editMode) return;
    setFormError("");
    setDoneMsg("");
    setSending(true);
    try {
      const res = await clinicAPI.createPublicTrialLead({
        full_name: name,
        phone,
        comment,
      });
      setDoneMsg(res.message || "Заявка отправлена");
      setName("");
      setPhone("");
      setComment("");
    } catch (err) {
      setFormError(err.response?.data?.error || "Не удалось отправить заявку");
    } finally {
      setSending(false);
    }
  };

  const updateHeadlineLine = (index, value) => {
    const next = [...headlineLines];
    next[index] = value;
    patchContent({ headline_lines: next });
  };

  const updateFeature = (index, field, value) => {
    const next = [...features];
    next[index] = { ...(next[index] || {}), [field]: value };
    patchContent({ features: next });
  };

  const updateReview = (index, field, value) => {
    const next = [...reviews];
    next[index] = { ...(next[index] || {}), [field]: value };
    patchContent({ reviews: next });
  };

  const updateBenefit = (index, value) => {
    const next = [...trialBenefits];
    next[index] = value;
    patchContent({ trial_benefits: next });
  };

  if (loading) {
    return (
      <div className={s.page} style={themeStyle}>
        <div className={s.loading}>Загрузка…</div>
      </div>
    );
  }

  const brandParts = clubName.split(/\s+/);
  const brandFirst = brandParts[0]?.toUpperCase() || clubName.toUpperCase();
  const brandRest = brandParts.slice(1).join(" ").toUpperCase();

  return (
    <div className={`${s.page} ${editMode ? s.pageEdit : ""}`} style={themeStyle}>
      <section className={s.hero} aria-label="Главный экран">
        <div
          className={s.heroMedia}
          style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
        >
          <div className={s.heroShade} />
        </div>
        <div className={s.heroStripe} aria-hidden />
        <div className={s.heroInner}>
          <div className={s.heroBadge}>
            {editMode ? (
              <Editable
                editMode
                value={eyebrow}
                placeholder="Подпись над заголовком"
                maxLength={80}
                onChange={(v) => patchContent({ eyebrow: v })}
                className={s.heroBadgeText}
              />
            ) : (
              <span className={s.heroBadgeText}>{eyebrow}</span>
            )}
          </div>
          <h1 className={s.heroHeadline}>
            {headlineLines.map((line, idx) =>
              editMode ? (
                <Editable
                  key={idx}
                  editMode
                  value={line}
                  placeholder={`Строка ${idx + 1}`}
                  maxLength={40}
                  onChange={(v) => updateHeadlineLine(idx, v)}
                  className={`${s.heroHeadlineLine} ${accentLineSet.has(idx) ? s.heroHeadlineAccent : ""}`}
                  as="span"
                />
              ) : (
                <span
                  key={idx}
                  className={`${s.heroHeadlineLine} ${accentLineSet.has(idx) ? s.heroHeadlineAccent : ""}`}
                >
                  {line}
                </span>
              ),
            )}
          </h1>
          {editMode ? (
            <Editable
              editMode
              value={subheadline}
              placeholder="Короткое описание"
              maxLength={400}
              onChange={(v) => patchContent({ subheadline: v })}
              className={s.heroLead}
              multiline
            />
          ) : (
            <p className={s.heroLead}>{subheadline}</p>
          )}
          <div className={s.heroActions}>
            {showForm ? (
              editMode ? (
                <span className={s.btnPrimary}>{ctaLabel}</span>
              ) : (
                <a href="#trial" className={s.btnPrimary}>
                  {ctaLabel}
                </a>
              )
            ) : null}
            <a href="#features" className={s.btnGhost}>
              {secondaryCtaLabel}
            </a>
          </div>
        </div>
      </section>

      <header className={s.navBar}>
        <div className={s.navInner}>
          <div className={s.navBrand}>
            {logoUrl ? <img src={logoUrl} alt="" className={s.navLogo} /> : null}
            <span className={s.navBrandAccent}>{brandFirst}</span>
            {brandRest ? <span className={s.navBrandRest}>{brandRest}</span> : null}
          </div>
          {sportTags.length ? (
            <div className={s.navTags}>
              {sportTags.map((tag, idx) => (
                <span key={tag} className={`${s.navTag} ${idx === 0 ? s.navTagActive : ""}`}>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
          <div className={s.navActions}>
            {showForm ? (
              editMode ? (
                <span className={s.navCta}>{ctaLabel.replace(/\s*→\s*$/, "")}</span>
              ) : (
                <a href="#trial" className={s.navCta}>
                  Записаться
                </a>
              )
            ) : null}
            {!editMode ? (
              <Link to={PORTALS.patient.login} className={s.navLogin}>
                Войти
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      {showStats ? (
        <section className={s.statsBand} aria-label="Статистика">
          <div className={s.container}>
            <div className={s.statsRow}>
              {stats.slice(0, 4).map((item, idx) => (
                <div key={`${item.value}-${idx}`} className={s.statCard}>
                  <strong>{item.value || "—"}</strong>
                  <span>{item.label || ""}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {blockOrder.map((blockId) => {
        if (blockId === "features" && showFeatures) {
          return (
            <section key={blockId} id="features" className={s.section}>
              <div className={s.container}>
                <div className={s.sectionIntro}>
                  <div className={s.sectionIntroMain}>
                    <Editable
                      editMode={editMode}
                      value={content.features_eyebrow || "Почему мы"}
                      placeholder="Подпись"
                      maxLength={40}
                      onChange={(v) => patchContent({ features_eyebrow: v })}
                      className={s.eyebrow}
                    />
                    <h2 className={s.displayTitle}>
                      <Editable
                        editMode={editMode}
                        value={content.features_title || "Что ты"}
                        placeholder="Заголовок"
                        maxLength={60}
                        onChange={(v) => patchContent({ features_title: v })}
                        className={s.displayTitleLine}
                        as="span"
                      />
                      <Editable
                        editMode={editMode}
                        value={content.features_title_accent || "получишь"}
                        placeholder="Акцент"
                        maxLength={60}
                        onChange={(v) => patchContent({ features_title_accent: v })}
                        className={`${s.displayTitleLine} ${s.displayTitleAccent}`}
                        as="span"
                      />
                    </h2>
                  </div>
                  <Editable
                    editMode={editMode}
                    value={content.features_text || ""}
                    placeholder="Короткий текст о клубе"
                    maxLength={400}
                    onChange={(v) => patchContent({ features_text: v })}
                    className={s.sectionLead}
                    multiline
                  />
                </div>
                <div className={s.featureGrid}>
                  {(features.length ? features : [{}, {}, {}, {}]).slice(0, 4).map((item, idx) => (
                    <article key={idx} className={s.featureCard}>
                      {editMode ? (
                        <input
                          className={`${s.featureIcon} ${s.editable}`}
                          value={item.icon || ""}
                          placeholder="🏀"
                          maxLength={8}
                          onChange={(e) => updateFeature(idx, "icon", e.target.value)}
                        />
                      ) : (
                        <div className={s.featureIcon}>{item.icon || "⭐"}</div>
                      )}
                      <Editable
                        editMode={editMode}
                        value={item.title || ""}
                        placeholder="Название"
                        maxLength={80}
                        onChange={(v) => updateFeature(idx, "title", v)}
                        className={s.featureTitle}
                        as="h3"
                      />
                      <Editable
                        editMode={editMode}
                        value={item.text || ""}
                        placeholder="Описание"
                        maxLength={200}
                        onChange={(v) => updateFeature(idx, "text", v)}
                        className={s.featureText}
                        multiline
                      />
                    </article>
                  ))}
                </div>
              </div>
            </section>
          );
        }

        if (blockId === "team" && showTeam) {
          return (
            <section key={blockId} className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.container}>
                <div className={s.teamTabs} role="tablist">
                  {TEAM_TABS.filter((tab) => tab.id !== "gallery" || showGallery).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={teamTab === tab.id}
                      className={`${s.teamTab} ${teamTab === tab.id ? s.teamTabActive : ""}`}
                      onClick={() => setTeamTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {teamTab === "trainers" ? (
                  <div className={s.trainerGrid}>
                    {(doctors.length ? doctors : editMode ? [{}] : []).slice(0, 6).map((doc, idx) => {
                      const photo = mediaUrl(doc.photo_url);
                      return (
                        <article key={doc.id || idx} className={s.trainerCard}>
                          <div className={s.trainerPhotoWrap}>
                            {photo ? (
                              <img src={photo} alt="" className={s.trainerPhoto} />
                            ) : (
                              <div className={s.trainerPhotoFallback} />
                            )}
                            <div className={s.trainerPhotoShade} />
                          </div>
                          <div className={s.trainerBody}>
                            <h3 className={s.trainerName}>
                              {(doc.full_name || "Тренер").toUpperCase()}
                            </h3>
                            {doc.specialization ? (
                              <p className={s.trainerRole}>{doc.specialization}</p>
                            ) : null}
                            {doc.experience_years ? (
                              <p className={s.trainerMeta}>
                                {doc.experience_years} лет опыта
                              </p>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}
                    {!doctors.length && !editMode ? (
                      <p className={s.emptyHint}>Добавьте тренеров в разделе «Команда».</p>
                    ) : null}
                  </div>
                ) : null}
                {teamTab === "schedule" ? (
                  <div className={s.schedulePanel}>
                    <p className={s.scheduleText}>
                      Расписание групп доступно после записи. Оставьте заявку — подберём подходящую
                      группу и время.
                    </p>
                    {!editMode ? (
                      <a href="#trial" className={s.btnPrimary}>
                        {ctaLabel}
                      </a>
                    ) : null}
                  </div>
                ) : null}
                {teamTab === "gallery" && showGallery ? (
                  gallery.length ? (
                    <div className={s.galleryGrid}>
                      {gallery.slice(0, 6).map((url, idx) => (
                        <img key={`${url}-${idx}`} src={url} alt="" className={s.galleryItem} />
                      ))}
                    </div>
                  ) : (
                    <p className={s.emptyHint}>
                      Галерея пуста — добавьте фото во вкладке «Персонализация».
                    </p>
                  )
                ) : null}
              </div>
            </section>
          );
        }

        if (blockId === "reviews" && showReviews) {
          return (
            <section key={blockId} className={s.section}>
              <div className={s.container}>
                <Editable
                  editMode={editMode}
                  value={content.reviews_eyebrow || "Отзывы"}
                  placeholder="Подпись"
                  maxLength={40}
                  onChange={(v) => patchContent({ reviews_eyebrow: v })}
                  className={s.eyebrow}
                />
                <h2 className={s.displayTitle}>
                  <Editable
                    editMode={editMode}
                    value={content.reviews_title || "Что говорят"}
                    placeholder="Заголовок"
                    maxLength={60}
                    onChange={(v) => patchContent({ reviews_title: v })}
                    className={s.displayTitleLine}
                    as="span"
                  />
                  <Editable
                    editMode={editMode}
                    value={content.reviews_title_accent || "наши ученики"}
                    placeholder="Акцент"
                    maxLength={60}
                    onChange={(v) => patchContent({ reviews_title_accent: v })}
                    className={`${s.displayTitleLine} ${s.displayTitleAccent}`}
                    as="span"
                  />
                </h2>
                <div className={s.reviewGrid}>
                  {(reviews.length ? reviews : [{}]).slice(0, 3).map((item, idx) => (
                    <article key={idx} className={s.reviewCard}>
                      <StarRating />
                      <Editable
                        editMode={editMode}
                        value={item.text || ""}
                        placeholder="Текст отзыва"
                        maxLength={400}
                        onChange={(v) => updateReview(idx, "text", v)}
                        className={s.reviewText}
                        multiline
                      />
                      <div className={s.reviewAuthor}>
                        <div className={s.reviewAvatar}>{initials(item.author)}</div>
                        <div>
                          <Editable
                            editMode={editMode}
                            value={item.author || ""}
                            placeholder="Имя"
                            maxLength={60}
                            onChange={(v) => updateReview(idx, "author", v)}
                            className={s.reviewName}
                          />
                          <Editable
                            editMode={editMode}
                            value={item.meta || ""}
                            placeholder="Возраст"
                            maxLength={40}
                            onChange={(v) => updateReview(idx, "meta", v)}
                            className={s.reviewMeta}
                          />
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          );
        }

        if (blockId === "trial" && showForm) {
          return (
            <section key={blockId} id="trial" className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.container}>
                <div className={s.trialGrid}>
                  <div className={s.trialCopy}>
                    <Editable
                      editMode={editMode}
                      value={content.trial_eyebrow || "Бесплатно"}
                      placeholder="Подпись"
                      maxLength={40}
                      onChange={(v) => patchContent({ trial_eyebrow: v })}
                      className={s.eyebrow}
                    />
                    <h2 className={s.displayTitle}>
                      <Editable
                        editMode={editMode}
                        value={content.trial_title || "ПРОБНОЕ"}
                        placeholder="Заголовок"
                        maxLength={60}
                        onChange={(v) => patchContent({ trial_title: v })}
                        className={s.displayTitleLine}
                        as="span"
                      />
                      <Editable
                        editMode={editMode}
                        value={content.trial_title_accent || "ЗАНЯТИЕ"}
                        placeholder="Акцент"
                        maxLength={60}
                        onChange={(v) => patchContent({ trial_title_accent: v })}
                        className={`${s.displayTitleLine} ${s.displayTitleAccent}`}
                        as="span"
                      />
                    </h2>
                    <Editable
                      editMode={editMode}
                      value={content.trial_text || ""}
                      placeholder="Текст о пробном"
                      maxLength={400}
                      onChange={(v) => patchContent({ trial_text: v })}
                      className={s.sectionLead}
                      multiline
                    />
                    <ul className={s.benefitList}>
                      {(trialBenefits.length ? trialBenefits : [""]).slice(0, 6).map((item, idx) => (
                        <li key={idx}>
                          <span className={s.benefitCheck} aria-hidden>
                            ✓
                          </span>
                          {editMode ? (
                            <input
                              className={`${s.benefitText} ${s.editable}`}
                              value={item || ""}
                              placeholder="Пункт списка"
                              maxLength={120}
                              onChange={(e) => updateBenefit(idx, e.target.value)}
                            />
                          ) : (
                            <span className={s.benefitText}>{item}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={s.trialFormCard}>
                    <Editable
                      editMode={editMode}
                      value={content.trial_form_title || "Оставь заявку"}
                      placeholder="Заголовок формы"
                      maxLength={60}
                      onChange={(v) => patchContent({ trial_form_title: v })}
                      className={s.formTitle}
                      as="h3"
                    />
                    {editMode ? (
                      <p className={s.editHint}>Форма заявки (в редакторе не отправляется)</p>
                    ) : doneMsg ? (
                      <p className={s.success}>{doneMsg}</p>
                    ) : (
                      <form className={s.form} onSubmit={onSubmit}>
                        <div className={s.formRow}>
                          <Field label="Имя">
                            <Input
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="Как к вам обращаться"
                              required
                              autoComplete="name"
                            />
                          </Field>
                          <Field label="Телефон">
                            <PhoneInput value={phone} onChange={setPhone} required />
                          </Field>
                        </div>
                        <Field label="Комментарий" hint="Необязательно">
                          <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Направление, возраст, удобное время…"
                            rows={3}
                          />
                        </Field>
                        {formError ? <p className={s.error}>{formError}</p> : null}
                        <button type="submit" disabled={sending} className={s.submitBtn}>
                          {sending ? "Отправляем…" : "Записаться на пробное"}
                        </button>
                        <Editable
                          editMode={editMode}
                          value={
                            content.trial_form_note ||
                            "Пробное занятие бесплатно. Без обязательств."
                          }
                          placeholder="Примечание под формой"
                          maxLength={120}
                          onChange={(v) => patchContent({ trial_form_note: v })}
                          className={s.formNote}
                        />
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        }

        if (blockId === "contacts" && showContacts) {
          return null;
        }

        return null;
      })}

      <footer className={s.footer}>
        <div className={s.container}>
          <div className={s.footerGrid}>
            <div className={s.footerBrandCol}>
              <div className={s.footerBrand}>
                <span className={s.navBrandAccent}>{brandFirst}</span>
                {brandRest ? <span className={s.navBrandRest}>{brandRest}</span> : null}
              </div>
              <Editable
                editMode={editMode}
                value={footerText}
                placeholder="Короткое описание клуба"
                maxLength={200}
                onChange={(v) => patchContent({ footer_text: v })}
                className={s.footerAbout}
                multiline
              />
            </div>
            {specializations.length ? (
              <div className={s.footerCol}>
                <strong className={s.footerColTitle}>Направления</strong>
                {specializations.slice(0, 6).map((spec) => (
                  <span key={spec.id} className={s.footerLink}>
                    {spec.name}
                  </span>
                ))}
              </div>
            ) : null}
            <div className={s.footerCol}>
              <strong className={s.footerColTitle}>
                {content.contacts_title || "Контакты"}
              </strong>
              {clinic?.phone ? <span className={s.footerLink}>{clinic.phone}</span> : null}
              {clinic?.address ? <span className={s.footerLink}>{clinic.address}</span> : null}
              <Editable
                editMode={editMode}
                value={contactsNote}
                placeholder="Email, соцсети, часы работы…"
                maxLength={400}
                onChange={(v) => patchContent({ contacts_note: v })}
                className={s.footerLink}
                multiline={editMode}
              />
            </div>
          </div>
          <div className={s.footerBottom}>
            <span>
              © {new Date().getFullYear()} {clubName}
            </span>
            <span>
              Сайт на платформе {L.productName}
              {LEGAL_PUBLIC_ENABLED ? (
                <>
                  {" · "}
                  <LegalDocLink doc="privacy">Конфиденциальность</LegalDocLink>
                  {" · "}
                  <LegalDocLink doc="offer">Оферта</LegalDocLink>
                </>
              ) : null}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
